import { useReducer, useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { s3DataService, DownloadProgress } from '../services/S3DataService';
import { transformer } from '../services/chartDataTransformer/transformer';

export type DataSource = 'jobs' | 'applications';
export type ShowAs = 'count' | 'average_time' | 'average';
export type DateRange = 'last_12_months' | 'last_3_months' | 'last_month' | 'last_year_quarters';
export type GroupBy =
  | 'location'
  | 'state'
  | 'status'
  | 'source_type'
  | 'source'
  | 'internal_external'
  | 'job_state'
  | 'published_to_hired'
  | 'first_application_to_hired'
  | 'requisition_approved_to_hired'
  | 'requisition_requested_to_hired'
  | 'job_reference'
  | 'published_to_first_application';

export interface Entity {
  id: number;
  name: string;
}

export interface StatusFilter {
  id: string;
  name: string;
}

export const ALL_OPTION: Entity = { id: 0, name: 'All' };
export const ALL_STATUSES_OPTION: StatusFilter = { id: '0', name: 'All' };

// Utility function to convert snake_case to title case
const formatStatusName = (statusName: string): string => {
  return statusName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface ChartState {
  dataSource: DataSource | '';
  showAs: ShowAs | '';
  groupBy: GroupBy | '';
  dateRange: DateRange;
  selectedEntities: Entity[];
  selectedStatuses: StatusFilter[];
}

type ChartAction =
  | { type: 'SET_DATA_SOURCE'; payload: DataSource }
  | { type: 'SET_SHOW_AS'; payload: ShowAs }
  | { type: 'SET_GROUP_BY'; payload: GroupBy }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_ENTITIES'; payload: Entity[] }
  | { type: 'SET_STATUSES'; payload: StatusFilter[] }
  | {
      type: 'SET_PRESET_FILTERS';
      payload: { dataSource: DataSource; showAs: ShowAs; groupBy: GroupBy; dateRange: DateRange };
    }
  | { type: 'RESET' };

const initialState: ChartState = {
  dataSource: '',
  showAs: '',
  groupBy: '',
  dateRange: 'last_12_months',
  selectedEntities: [ALL_OPTION],
  selectedStatuses: [ALL_STATUSES_OPTION]
};

function chartReducer(state: ChartState, action: ChartAction): ChartState {
  switch (action.type) {
    case 'SET_DATA_SOURCE':
      return {
        ...state,
        dataSource: action.payload,
        showAs: '',
        groupBy: '',
        selectedEntities: [ALL_OPTION]
      };
    case 'SET_SHOW_AS':
      return {
        ...state,
        showAs: action.payload,
        groupBy: '',
        selectedEntities: [ALL_OPTION]
      };
    case 'SET_GROUP_BY':
      return {
        ...state,
        groupBy: action.payload
      };
    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: action.payload
      };
    case 'SET_ENTITIES':
      return {
        ...state,
        selectedEntities: action.payload
      };
    case 'SET_STATUSES':
      return {
        ...state,
        selectedStatuses: action.payload
      };
    case 'SET_PRESET_FILTERS':
      return {
        ...state,
        dataSource: action.payload.dataSource,
        showAs: action.payload.showAs,
        groupBy: action.payload.groupBy,
        dateRange: action.payload.dateRange,
        selectedEntities: [ALL_OPTION]
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function useChartData(entities: Entity[]) {
  const [state, dispatch] = useReducer(chartReducer, initialState);
  const { dataSource, showAs, groupBy, dateRange, selectedEntities, selectedStatuses } = state;

  // State for tracking download progress
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    total: 0,
    completed: 0,
    percentage: 0,
    status: 'idle'
  });

  // State for storing downloaded data
  const [downloadedData, setDownloadedData] = useState<any>(null);

  // State to track if initial data load has been attempted
  const [initialLoadAttempted, setInitialLoadAttempted] = useState<boolean>(false);

  // Get available statuses from the downloaded data
  const availableStatuses = useMemo(() => {
    if (!downloadedData || (dataSource !== 'applications' && dataSource !== 'jobs')) {
      return [ALL_STATUSES_OPTION];
    }

    const statuses = new Set<string>();

    Object.entries(downloadedData).forEach(([key, value]: [string, any]) => {
      if (Array.isArray(value.data)) {
        value.data.forEach((record: any) => {
          if (record.status) {
            statuses.add(record.status);
          }
        });
      }
    });

    return [
      ALL_STATUSES_OPTION,
      ...Array.from(statuses).map((status) => ({ id: status, name: formatStatusName(status) }))
    ];
  }, [downloadedData, dataSource]);

  // Function to download past year data
  const downloadPastYearData = useCallback(
    async (selectedEntities: Entity[]) => {
      if (
        !dataSource ||
        !['applications', 'jobs'].includes(dataSource) ||
        !selectedEntities.length
      ) {
        return;
      }
      // Get current status to check if download is in progress
      const currentStatus = downloadProgress.status;

      // Prevent repeated work if a download is already in progress
      if (currentStatus === 'downloading' || currentStatus === 'processing') {
        console.log('Download already in progress, ignoring request');
        return;
      }

      // Reset progress
      setDownloadProgress({
        total: 0,
        completed: 0,
        percentage: 0,
        status: 'downloading'
      });

      try {
        // Get entity IDs, filtering out the "All" option
        const entityIds = selectedEntities
          .filter((entity) => entity.id !== 0)
          .map((entity) => entity.id);

        // If "All" is selected or no specific entities are selected, use all available entities
        const idsToDownload =
          entityIds.length > 0
            ? entityIds
            : entities.filter((entity) => entity.id !== 0).map((entity) => entity.id);

        if (idsToDownload.length === 0) {
          console.warn('No entities to download data for');
          setDownloadProgress({
            total: 0,
            completed: 0,
            percentage: 100,
            status: 'complete'
          });
          return;
        } else console.log('total entities to download: ', idsToDownload.length, downloadProgress);

        // Download data
        const data = await s3DataService.downloadPastYearData({
          entityIds: idsToDownload,
          recordType: dataSource,
          onProgress: setDownloadProgress
        });

        // Validate the downloaded data
        if (!data || Object.keys(data).length === 0) {
          console.error('Downloaded data is empty or invalid');
          setDownloadProgress((prev) => ({
            ...prev,
            status: 'error',
            error: 'No data available for the selected criteria'
          }));
          return;
        }
        // Store downloaded data
        setDownloadedData(data);

        return data;
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error('Error downloading past year data:', error.message);
        } else {
          console.error('Unknown error occurred');
        }
        setDownloadProgress((prev) => ({
          ...prev,
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to download data from S3'
        }));

        // Clear any partial data to avoid displaying incorrect charts
        setDownloadedData(null);
      }
    },
    [dataSource, downloadProgress.status, entities] // Removed downloadProgress.status from dependencies
  );

  // Function to cancel download
  const cancelDownload = useCallback(() => {
    // Currently, there's no direct way to cancel fetch requests
    // This just updates the UI to indicate cancellation
    setDownloadProgress((prev) => ({
      ...prev,
      status: 'idle'
    }));
  }, []);

  // Function to dismiss error messages
  const dismissError = useCallback(() => {
    setDownloadProgress((prev) => ({
      ...prev,
      status: 'idle',
      error: undefined
    }));
  }, []);

  // Automatically download data from S3 when Data Source is selected
  useEffect(() => {
    // Only attempt to download if we haven't tried yet and a valid data source is selected
    if (
      !initialLoadAttempted &&
      ['applications', 'jobs'].includes(dataSource) &&
      selectedEntities.length > 0
    ) {
      setInitialLoadAttempted(true);
      downloadPastYearData(selectedEntities);
    }
  }, [dataSource, initialLoadAttempted, selectedEntities]);

  // Separate effect to handle filter changes
  useEffect(() => {
    // Only download data when filters change if we've already done the initial load
    if (
      initialLoadAttempted &&
      ['applications', 'jobs'].includes(dataSource) &&
      selectedEntities.length > 0
    ) {
      downloadPastYearData(selectedEntities);
    }
  }, [selectedEntities, initialLoadAttempted, dataSource, dateRange]);

  const isPieChart = useMemo(
    () => dataSource === 'applications' && (groupBy === 'source_type' || groupBy === 'source'),
    [dataSource, groupBy]
  );

  const chartData = useMemo(() => {
    // If we have downloaded data, use it for both applications and jobs
    if (downloadedData && (dataSource === 'applications' || dataSource === 'jobs')) {
      return transformer(
        dataSource,
        showAs,
        groupBy,
        downloadedData,
        selectedEntities,
        dateRange,
        selectedStatuses
      );
    }
  }, [downloadedData, dataSource, showAs, groupBy, selectedEntities, dateRange, selectedStatuses]);

  const getShowAsOptions = (): { value: ShowAs; label: string }[] => {
    // For applications, return both 'count' and 'average'
    if (dataSource === 'applications') {
      return [
        { value: 'count' as ShowAs, label: 'Count' },
        { value: 'average' as ShowAs, label: 'Average' }
      ];
    }

    // For jobs, check if downloadedData contains time-related metrics
    if (dataSource === 'jobs' && downloadedData?.jobs) {
      return [
        { value: 'count' as ShowAs, label: 'Count' },
        { value: 'average_time' as ShowAs, label: 'Average Time' }
      ];
    }

    // Default options if no data is available yet
    return [
      { value: 'count' as ShowAs, label: 'Count' },
      { value: 'average_time' as ShowAs, label: 'Average Time' }
    ];
  };

  const getGroupByOptions = (): { value: GroupBy; label: string }[] => {
    if (dataSource === 'applications') {
      if (showAs === 'average') {
        return [{ value: 'job_reference' as GroupBy, label: 'Applications per Job' }];
      }
      return [
        { value: 'status' as GroupBy, label: 'Application Status' },
        { value: 'source' as GroupBy, label: 'Source' },
        { value: 'source_type' as GroupBy, label: 'Source Type' }
      ];
    }

    if (dataSource === 'jobs') {
      if (showAs === 'count') {
        return [
          { value: 'location' as GroupBy, label: 'Location' },
          { value: 'internal_external' as GroupBy, label: 'Internal/External' },
          { value: 'job_state' as GroupBy, label: 'Job State' }
        ];
      }
      if (showAs === 'average_time') {
        return [
          { value: 'published_to_hired' as GroupBy, label: 'Job Published date to Hired' },
          {
            value: 'first_application_to_hired' as GroupBy,
            label: 'First Application date to Hired'
          },
          {
            value: 'requisition_approved_to_hired' as GroupBy,
            label: 'Requisition Approved to Hired'
          },
          {
            value: 'requisition_requested_to_hired' as GroupBy,
            label: 'Requisition Requested to Hired'
          }
        ];
      }
    }

    return [];
  };

  const getDateRangeOptions = (): { value: DateRange; label: string }[] => {
    return [
      { value: 'last_12_months' as DateRange, label: 'Last 12 months' },
      { value: 'last_3_months' as DateRange, label: 'Last 3 months' },
      { value: 'last_month' as DateRange, label: 'Last month' },
      { value: 'last_year_quarters' as DateRange, label: 'Last Year in Quarters' }
    ];
  };

  return {
    state,
    dispatch,
    chartData,
    isPieChart,
    getShowAsOptions,
    getGroupByOptions,
    getDateRangeOptions,
    // Download-related properties and functions
    downloadProgress,
    downloadPastYearData,
    cancelDownload,
    dismissError,
    hasDownloadedData: !!downloadedData,
    availableStatuses
  };
}
